/**
 * The demand curve the projection walks: how much of a key's cumulative forecast has been consumed
 * by a given week, and the inverse.
 *
 * Shared by the projection engine and the stockout engine in `calculateStockoutTimes.ts`:
 * `getDemandConsumedByWeek` and `getWeekOfReachingDemand` must stay a pair, or a crossing and a
 * stock level disagree about when stock ran out.
 */
import { getForecastsForWeeks, getWeekOfHittingThreshold } from "~/utils/forecastUtils"

// Demand consumed between week start and the anchor: min(sales, weekForecast) when sales
// are known (remaining is then max(0, forecast - sales); an oversold week never reduces
// later weeks), otherwise the prorated fallback.
export function getElapsedWeekDemand(
  forecasts: number[],
  stockWeek: number,
  salesWeekToDate: number | undefined,
) {
  if (salesWeekToDate === undefined) {
    return stockWeek ? getForecastsForWeeks(forecasts, stockWeek) : 0
  }
  return Math.min(salesWeekToDate, forecasts[0] ?? 0)
}

/**
 * Cumulative demand consumed from the start of the current week up to `week`, in the time base the
 * walk reports crossings in.
 *
 * Inside the current week with measured sales, the Monday curve is not a usable intra-week schedule
 * — an oversold week puts the curve *behind* what has actually been sold — so the week's remainder
 * is spread evenly over the days still to come. Everywhere else this is exactly
 * `getForecastsForWeeks`. `getWeekOfReachingDemand` is its inverse.
 */
export function getDemandConsumedByWeek(
  forecasts: number[],
  week: number,
  stockWeek: number,
  salesWeekToDate: number | undefined,
) {
  const elapsed = getElapsedWeekDemand(forecasts, stockWeek, salesWeekToDate)
  const remaining = (forecasts[0] ?? 0) - elapsed
  if (salesWeekToDate === undefined || stockWeek >= 1 || remaining <= 0 || week >= 1) {
    return getForecastsForWeeks(forecasts, week)
  }
  if (week <= stockWeek) return elapsed
  return elapsed + (remaining * (week - stockWeek)) / (1 - stockWeek)
}

/** Inverse of `getDemandConsumedByWeek`: the week at which that much demand has been consumed. */
export function getWeekOfReachingDemand(
  forecasts: number[],
  demand: number,
  stockWeek: number,
  salesWeekToDate: number | undefined,
) {
  const raw = getWeekOfHittingThreshold(demand, forecasts)
  const elapsed = getElapsedWeekDemand(forecasts, stockWeek, salesWeekToDate)
  const remaining = (forecasts[0] ?? 0) - elapsed
  if (salesWeekToDate === undefined || stockWeek >= 1 || remaining <= 0 || !(raw < 1)) {
    return raw
  }
  return stockWeek + ((1 - stockWeek) * (demand - elapsed)) / remaining
}

/**
 * Demand still ahead of today at `week` — the engine's `demand` component, and the only demand any
 * stock level is allowed to charge. Derived from `getDemandConsumedByWeek` rather than the raw
 * Monday curve so levels, crossings and components share one schedule inside the current week.
 */
export function demandAheadOfToday(
  forecasts: number[],
  week: number,
  stockWeek: number,
  salesWeekToDate: number | undefined,
) {
  return (
    getDemandConsumedByWeek(forecasts, week, stockWeek, salesWeekToDate) -
    getElapsedWeekDemand(forecasts, stockWeek, salesWeekToDate)
  )
}
